import { useState } from 'react';
import {
  getFinancialReviewDetail,
  listAdminDeposits,
  listFinancialReviews,
  postDirectRefund,
  resolveFinancialReview,
  type AdminDepositsResponse,
  type FinancialReviewDetailResponse,
  type FinancialReviewListResponse,
  type FinancialReviewResolveResponse,
  type ResolveReviewInput,
  type WireFinancialResolution,
  type WireRefundReason,
  type WireReviewSource,
  type WireReviewStatus,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IdMono,
  Input,
  LoadingState,
  Modal,
  PageHead,
  Select,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';

const STATUS_FILTERS: readonly WireReviewStatus[] = ['open', 'escalated', 'resolved', 'rejected'];
const SOURCE_FILTERS: readonly WireReviewSource[] = [
  'reconciliation_mismatch',
  'payment_dispute',
  'refund_approval',
  'delivery_failure',
];
const RESOLUTIONS: readonly WireFinancialResolution[] = [
  'no_action', 'refund_posted', 'credit_issued', 'adjustment_posted', 'rejected', 'escalated',
];
const REFUND_REASONS: readonly WireRefundReason[] = [
  'customer_request', 'operator_goodwill', 'chargeback_received',
  'duplicate_charge', 'delivery_failure', 'review_resolution',
];

function statusTone(s: WireReviewStatus | string): BadgeTone {
  if (s === 'open' || s === 'escalated') return 'warn';
  if (s === 'resolved') return 'success';
  if (s === 'rejected') return 'danger';
  return 'neutral';
}

function refundTone(s: string): BadgeTone {
  if (s === 'posted') return 'success';
  if (s === 'pending') return 'warn';
  if (s === 'failed') return 'danger';
  return 'neutral';
}

export function FinancialReviewsPage(): JSX.Element {
  const canRead = useCan('financial:review');
  const canRefund = useCan('financial:refund');
  const [tab, setTab] = useState<'deposits' | 'reviews'>('deposits');
  const [statusFilter, setStatusFilter] = useState<WireReviewStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<WireReviewSource | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const list = useApiQuery<FinancialReviewListResponse>(
    (o) =>
      listFinancialReviews(o, {
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        limit: 50,
      }),
    [statusFilter, sourceFilter],
  );

  return (
    <div>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Ödeme İncelemeleri</>}
        title="Ödeme İncelemeleri"
        subtitle="Mutabakat uyumsuzlukları, ödeme itirazları, iade onayları ve teslimat başarısızlıkları için operatör kuyruğu. Reconciler bir kayıt açar; ledger sessizce uyumsuzluğu kapatmaz."
        actions={
          canRefund ? (
            <Button variant="primary" icon={<Icon.Coins size={14} />} onClick={() => setShowRefundModal(true)}>
              İade başlat
            </Button>
          ) : null
        }
      />

      {!canRead ? <ErrorState reason="capability_missing:financial:review" /> : null}

      {canRead ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Button
              variant={tab === 'deposits' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTab('deposits')}
            >
              Para Yatırmalar
            </Button>
            <Button
              variant={tab === 'reviews' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTab('reviews')}
            >
              Manuel İnceleme
            </Button>
          </div>

          {tab === 'deposits' ? <DepositsTab /> : null}

          {tab === 'reviews' ? (
          <>
          <Card>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label htmlFor="rev-status" className="field-label">Status</label>
                <Select
                  id="rev-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as WireReviewStatus | '')}
                  style={{ width: 180 }}
                >
                  <option value="">outstanding</option>
                  {STATUS_FILTERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="rev-source" className="field-label">Source</label>
                <Select
                  id="rev-source"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value as WireReviewSource | '')}
                  style={{ width: 230 }}
                >
                  <option value="">any</option>
                  {SOURCE_FILTERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => list.reload()}>
                Yenile
              </Button>
            </div>
          </Card>

          {list.kind === 'loading' ? <LoadingState /> : null}
          {list.kind === 'error' ? <ErrorState reason={list.reason} onRetry={list.reload} /> : null}
          {list.kind === 'ok' ? (
            <ReviewList
              entries={list.data.entries}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          ) : null}

          {selectedId ? (
            <div style={{ marginTop: 16 }}>
              <ReviewDetail
                reviewCaseId={selectedId}
                canRefund={canRefund}
                onResolved={() => {
                  setSelectedId(null);
                  list.reload();
                }}
              />
            </div>
          ) : null}
          </>
          ) : null}
        </>
      ) : null}

      <Modal
        open={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          list.reload();
        }}
        title="Direct refund"
        width={620}
        testId="direct-refund-modal"
      >
        <DirectRefundBody onDone={() => {
          setShowRefundModal(false);
          list.reload();
        }} />
      </Modal>
    </div>
  );
}

function ReviewList({
  entries,
  selectedId,
  onSelect,
}: {
  readonly entries: FinancialReviewListResponse['entries'];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <Card>
        <EmptyState
          label="Bu filtreyle eşleşen inceleme kaydı yok."
          hint="Daha fazla görmek için durum veya kaynak filtresini değiştirin."
        />
      </Card>
    );
  }
  return (
    <Card flush>
      <CardHeader title={`Cases — ${entries.length}`} />
      <CardBody tight style={{ padding: 0 }}>
        <Table dense>
          <THead>
            <TRow>
              <TH>Opened</TH>
              <TH>Source</TH>
              <TH>Status</TH>
              <TH>Reason</TH>
              <TH>Checkout</TH>
              <TH>Actor</TH>
              <TH></TH>
            </TRow>
          </THead>
          <TBody>
            {entries.map((e) => (
              <TRow key={e.reviewCaseId} className={selectedId === e.reviewCaseId ? 'active' : ''}>
                <TD className="t-mono t-xs">{e.openedAt}</TD>
                <TD className="t-mono t-xs">{e.source}</TD>
                <TD><Badge tone={statusTone(e.status)} dot>{e.status}</Badge></TD>
                <TD className="t-mono t-xs">{e.reason}</TD>
                <TD>{e.checkoutIntentId ? <IdMono value={e.checkoutIntentId} /> : '—'}</TD>
                <TD>{e.actorId ? <IdMono value={e.actorId} /> : '—'}</TD>
                <TD>
                  <Button variant="ghost" size="sm" onClick={() => onSelect(e.reviewCaseId)} iconRight={<Icon.ChevronRight size={12} />}>
                    Aç
                  </Button>
                </TD>
              </TRow>
            ))}
          </TBody>
        </Table>
      </CardBody>
    </Card>
  );
}

function ReviewDetail({
  reviewCaseId,
  canRefund,
  onResolved,
}: {
  readonly reviewCaseId: string;
  readonly canRefund: boolean;
  readonly onResolved: () => void;
}): JSX.Element {
  const detail = useApiQuery<FinancialReviewDetailResponse>(
    (o) => getFinancialReviewDetail(o, reviewCaseId),
    [reviewCaseId],
  );
  if (detail.kind === 'loading') return <LoadingState />;
  if (detail.kind === 'error') {
    return <ErrorState reason={detail.reason} onRetry={detail.reload} />;
  }
  if (detail.kind !== 'ok') return <></>;
  const { case: c, financial, refunds } = detail.data;

  return (
    <Card flush>
      <CardHeader
        title={<>Case <IdMono value={c.reviewCaseId} /></>}
        actions={<Badge tone={statusTone(c.status)} dot>{c.status}</Badge>}
      />
      <CardBody>
        <div className="dl-rows">
          <Row k="Source" v={<span className="t-mono">{c.source}</span>} />
          <Row k="Reason" v={<span className="t-mono">{c.reason}</span>} />
          <Row k="Detail" v={<span className="t-mono">{c.detail ?? '—'}</span>} />
          <Row k="Checkout" v={c.checkoutIntentId ? <IdMono value={c.checkoutIntentId} /> : '—'} />
          <Row k="Actor" v={c.actorId ? <IdMono value={c.actorId} /> : '—'} />
          <Row k="Opened at" v={<span className="t-mono t-xs">{c.openedAt}</span>} />
          {c.resolvedAt ? (
            <>
              <Row k="Resolved at" v={<span className="t-mono t-xs">{c.resolvedAt}</span>} />
              <Row k="Resolved by" v={<span className="t-mono">{c.resolvedByActorId ?? '—'}</span>} />
              <Row k="Resolution" v={<span className="t-mono">{c.resolution ?? '—'}</span>} />
              <Row k="Note" v={c.resolutionNote ?? '—'} />
            </>
          ) : null}
        </div>

        {financial ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Financial context</h3>
            <div className="dl-rows">
              <Row k="Financial state" v={<span className="t-mono">{financial.financialState}</span>} />
              <Row k="Paid (minor)" v={<span className="t-mono t-num">{financial.paidAmountMinor}</span>} />
              <Row k="Refunded (minor)" v={<span className="t-mono t-num">{financial.refundedAmountMinor}</span>} />
              <Row k="Currency" v={<span className="t-mono">{financial.currency}</span>} />
              <Row k="Ledger entries" v={<span className="t-mono t-xs" style={{ wordBreak: 'break-all' }}>{financial.ledgerEntryIds.join(', ') || '—'}</span>} />
            </div>
          </div>
        ) : null}

        {refunds.length > 0 ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Bağlı iade istekleri</h3>
            <Table dense>
              <THead>
                <TRow><TH>ID</TH><TH>Status</TH><TH>Amount</TH><TH>Reason</TH><TH>Decided</TH></TRow>
              </THead>
              <TBody>
                {refunds.map((r) => (
                  <TRow key={r.refundRequestId}>
                    <TD><IdMono value={r.refundRequestId} /></TD>
                    <TD><Badge tone={refundTone(r.status)} dot>{r.status}</Badge></TD>
                    <TD className="t-mono t-num">{r.amountMinor} {r.currency}</TD>
                    <TD className="t-mono t-xs">{r.reason}</TD>
                    <TD className="t-mono t-xs">{r.decidedAt ?? '—'}</TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </div>
        ) : null}

        {c.status === 'open' || c.status === 'escalated' ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <ResolveForm
              reviewCaseId={c.reviewCaseId}
              canRefund={canRefund}
              currency={financial?.currency ?? 'USD'}
              onResolved={onResolved}
            />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ResolveForm({
  reviewCaseId,
  canRefund,
  currency,
  onResolved,
}: {
  readonly reviewCaseId: string;
  readonly canRefund: boolean;
  readonly currency: string;
  readonly onResolved: () => void;
}): JSX.Element {
  const [resolution, setResolution] = useState<WireFinancialResolution>('no_action');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [refundReason, setRefundReason] = useState<WireRefundReason>('review_resolution');
  const mutation = useApiMutation<
    { readonly reviewCaseId: string; readonly input: ResolveReviewInput },
    FinancialReviewResolveResponse
  >((o, x) => resolveFinancialReview(o, x.reviewCaseId, x.input));

  const requiresRefund = resolution === 'refund_posted';
  const canSubmit = !requiresRefund || (canRefund && amount !== '' && Number(amount) > 0);

  async function onSubmit(): Promise<void> {
    await mutation.run({
      reviewCaseId,
      input: {
        resolution,
        resolutionNote: note || null,
        refund: requiresRefund
          ? {
              amountMinor: Math.trunc(Number(amount)),
              currency,
              reason: refundReason,
              memo: note || null,
            }
          : undefined,
      },
    });
  }

  if (mutation.state.kind === 'ok') {
    onResolved();
    return <Banner tone="success">Resolved.</Banner>;
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Resolve</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Resolution" htmlFor="resolution">
          <Select id="resolution" value={resolution} onChange={(e) => setResolution(e.target.value as WireFinancialResolution)}>
            {RESOLUTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Note" htmlFor="note">
          <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="operator-safe note" />
        </Field>
        {requiresRefund ? (
          <>
            <Field label="Refund amount (minor)" htmlFor="amt" help={`e.g. 500 = ${currency} 5.00`}>
              <Input
                id="amt"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="numeric"
              />
            </Field>
            <Field label="Refund reason" htmlFor="rfreason">
              <Select id="rfreason" value={refundReason} onChange={(e) => setRefundReason(e.target.value as WireRefundReason)}>
                {REFUND_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            </Field>
          </>
        ) : null}
        <div>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.state.kind === 'running'}
            onClick={() => void onSubmit()}
            icon={<Icon.Check size={14} />}
          >
            Apply resolution
          </Button>
          {mutation.state.kind === 'error' ? (
            <div style={{ marginTop: 8 }}>
              <Banner tone="danger">{mutation.state.reason}</Banner>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DirectRefundBody({ onDone }: { readonly onDone: () => void }): JSX.Element {
  const [checkoutIntentId, setCheckoutIntentId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reason, setReason] = useState<WireRefundReason>('operator_goodwill');
  const [memo, setMemo] = useState('');
  const mutation = useApiMutation(postDirectRefund);

  async function onSubmit(): Promise<void> {
    await mutation.run({
      checkoutIntentId,
      amountMinor: Math.trunc(Number(amount)),
      currency,
      reason,
      memo: memo || null,
    });
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px', fontSize: 13.5 }}>
        Admin-direct refund. Creates a refund request AND posts the ledger row in one step.
        For reviewable cases use the review queue instead.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Checkout intent" htmlFor="ref-ci">
          <Input id="ref-ci" value={checkoutIntentId} onChange={(e) => setCheckoutIntentId(e.target.value)} />
        </Field>
        <Field label="Amount (minor)" htmlFor="ref-amt">
          <Input id="ref-amt" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Currency" htmlFor="ref-cur">
          <Input id="ref-cur" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </Field>
        <Field label="Reason" htmlFor="ref-reason">
          <Select id="ref-reason" value={reason} onChange={(e) => setReason(e.target.value as WireRefundReason)}>
            {REFUND_REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Memo" htmlFor="ref-memo">
          <Input id="ref-memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onDone}>İptal</Button>
          <Button
            variant="primary"
            loading={mutation.state.kind === 'running'}
            onClick={() => void onSubmit()}
            icon={<Icon.Send size={14} />}
          >
            Post refund
          </Button>
        </div>
        {mutation.state.kind === 'error' ? (
          <Banner tone="danger">{mutation.state.reason}</Banner>
        ) : null}
        {mutation.state.kind === 'ok' ? (
          <Banner tone="success">
            Posted. Ledger entry <code>{mutation.state.data.ledgerEntryId}</code>.
          </Banner>
        ) : null}
      </div>
    </div>
  );
}

function depositTone(s: string): BadgeTone {
  if (s === 'completed') return 'success';
  if (s === 'pending_payment') return 'warn';
  if (s === 'failed') return 'danger';
  return 'neutral';
}

const DEPOSIT_STATUSES = [
  'pending_payment',
  'completed',
  'failed',
  'expired',
  'cancelled',
] as const;

/**
 * Para Yatırmalar — V1 deposit-history parity. Lists EVERY user who created a
 * top-up / webhook deposit (customer_topup_intents joined to users). Default
 * status filter is ALL (every deposit today is pending_payment; defaulting to
 * `completed` would reproduce the old empty page). amountUsd is genuine cents.
 */
function DepositsTab(): JSX.Element {
  const [status, setStatus] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const q = useApiQuery<AdminDepositsResponse>(
    (o) => listAdminDeposits(o, { status: status || undefined, limit, offset }),
    [status, offset],
  );

  return (
    <>
      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="dep-status" className="field-label">Durum</label>
            <Select
              id="dep-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
              style={{ width: 200 }}
            >
              <option value="">tümü</option>
              {DEPOSIT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </div>
          <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => q.reload()}>
            Yenile
          </Button>
        </div>
      </Card>

      {q.kind === 'loading' ? <LoadingState /> : null}
      {q.kind === 'error' ? <ErrorState reason={q.reason} onRetry={q.reload} /> : null}
      {q.kind === 'ok' && q.data.entries.length === 0 ? (
        <Card>
          <EmptyState
            label="Bu filtreyle para yatırma kaydı yok."
            hint="Webhook oluşturan / yatırım yapan tüm kullanıcılar burada listelenir."
          />
        </Card>
      ) : null}
      {q.kind === 'ok' && q.data.entries.length > 0 ? (
        <Card flush>
          <CardHeader title={`Para yatırmalar — ${q.data.total}`} />
          <CardBody tight style={{ padding: 0 }}>
            <Table dense>
              <THead>
                <TRow>
                  <TH>Tarih</TH>
                  <TH>Kullanıcı</TH>
                  <TH>Tutar</TH>
                  <TH>Yöntem</TH>
                  <TH>Sağlayıcı</TH>
                  <TH>Durum</TH>
                  <TH>Ref</TH>
                  <TH>Ödeme ekranı</TH>
                </TRow>
              </THead>
              <TBody>
                {q.data.entries.map((d) => (
                  <TRow key={d.id}>
                    <TD className="t-mono t-xs">{new Date(d.createdAt).toLocaleString()}</TD>
                    <TD>
                      {d.user?.email ?? d.user?.username ?? d.user?.displayName ?? (
                        <IdMono value={d.actorId} />
                      )}
                    </TD>
                    <TD className="t-mono t-num">
                      ${d.amountUsd.toFixed(2)} {d.currency}
                    </TD>
                    <TD className="t-mono t-xs">{d.methodId}</TD>
                    <TD className="t-mono t-xs">{d.providerName ?? '—'}</TD>
                    <TD>
                      <Badge tone={depositTone(d.status)} dot>{d.status}</Badge>
                    </TD>
                    <TD>{d.providerIntentId ? <IdMono value={d.providerIntentId} /> : '—'}</TD>
                    <TD>
                      {d.redirectUrl ? (
                        <a
                          href={d.redirectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                          title="Cryptomus ödeme ekranını yeni sekmede aç"
                        >
                          Ödeme ekranı ↗
                        </a>
                      ) : (
                        '—'
                      )}
                    </TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      ) : null}
      {q.kind === 'ok' && q.data.total > limit ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset <= 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Önceki
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + limit >= q.data.total}
            onClick={() => setOffset(offset + limit)}
          >
            Sonraki
          </Button>
        </div>
      ) : null}
    </>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}
